import * as React from 'react'
import { ReactElement, useEffect, useRef, useState } from 'react'
import { Button, Field, Input, makeStyles, Text, tokens } from '@fluentui/react-components'
import { bundleIcon, FolderFilled, FolderRegular } from '@fluentui/react-icons'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import useAxios from '../../../hooks/useAxios'
import LoadingIcon from '../../../components/Icon/LoadingIcon'
import FolderBrowseDialog from './FolderBrowseDialog'
import { DRIVE_ITEM_CONFIG, findFolderByName, FolderPathAction, FolderPathIntent, getFullPath, sanitiseFolderName } from './folderPath'

const BrowseIcon = bundleIcon(FolderFilled, FolderRegular)

/** How long the user may keep typing before we ask SharePoint about the name. */
const CHECK_DEBOUNCE_MS = 400

/**
 * Answers whether a folder is already claimed by a dossier or a CRM relation. Resolves with the
 * name of whatever is occupying it, or undefined when the folder is free. Supplied by the form,
 * because a dossier and a party ask different endpoints.
 */
export type FolderUsageCheck = (item: DriveItem, signal: AbortSignal) => Promise<string | undefined>

export enum FolderPathStatus {
    /** Resolving the stored folder or the parent; nothing to show yet. */
    Loading = 'LOADING',
    /** A check is in flight for what the user just typed. */
    Checking = 'CHECKING',
    /** Free name, no folder stored yet: one will be created on save. */
    Create = 'CREATE',
    /** Free name, folder already stored: it will be renamed on save. */
    Rename = 'RENAME',
    /** The name still matches the stored folder; saving changes nothing. */
    Unchanged = 'UNCHANGED',
    /** The folder exists and nothing claims it, but the user has to say so explicitly. */
    ExistsFree = 'EXISTS_FREE',
    /** The user chose to point at that existing folder. */
    Adopted = 'ADOPTED',
    /** The folder exists and another dossier or relation already points at it. Blocking. */
    ExistsInUse = 'EXISTS_IN_USE',
    /** Something with that name exists but is a file. Blocking. */
    NotFolder = 'NOT_FOLDER',
    /** Nothing usable left after sanitising. Blocking. */
    Empty = 'EMPTY',
    /** Graph could not be reached. Blocking. */
    Error = 'ERROR',
}

const BLOCKING_STATUSES: FolderPathStatus[] = [
    FolderPathStatus.ExistsFree,
    FolderPathStatus.ExistsInUse,
    FolderPathStatus.NotFolder,
    FolderPathStatus.Empty,
    FolderPathStatus.Error,
]

type Props = {
    /** Drive item id of the folder stored on the entity. Absent while creating one. */
    itemId?: string
    /** Folder the target lives under. Absent means the drive root. */
    parentId?: string
    /** Name to prefill with. Only applied while the user has not edited the field. */
    suggestedName?: string
    /** Reports the intent to carry out on save, or undefined while the field is unusable. */
    onChange: (intent: FolderPathIntent | undefined, errorMessage?: string) => void
    /** Asks the backend whether an existing folder is already claimed. */
    checkUsage?: FolderUsageCheck
    /**
     * Show an empty field as invalid even when the user never touched it. Set by the form on a
     * submit attempt; until then an untouched empty field simply stays blank (HAM-4343).
     */
    validateEmpty?: boolean
    label?: string
    required?: boolean
    disabled?: boolean
}

const useStyles = makeStyles({
    prefix: {
        color: tokens.colorNeutralForeground3,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        direction: 'rtl',
    },
    // Sits inline in the validation message rather than reading as a second control.
    inlineAction: {
        minWidth: 'auto',
        paddingLeft: tokens.spacingHorizontalXS,
        paddingRight: tokens.spacingHorizontalXS,
    },
})

export default function FolderPathField(props: Props): ReactElement {
    const axios = useAxios()
    const { t: translate } = useTranslation()
    const styles = useStyles()

    const [status, setStatus] = useState<FolderPathStatus>(props.itemId ? FolderPathStatus.Loading : FolderPathStatus.Empty)
    const [name, setName] = useState<string>('')
    /** The folder the name currently resolves to, if any. Drives Adopt and Unchanged. */
    const [match, setMatch] = useState<DriveItem>()
    /** Who is occupying the matched folder, for the blocking message. */
    const [occupiedBy, setOccupiedBy] = useState<string>()
    const [errorDetail, setErrorDetail] = useState<string>()
    /** Name the stored folder had when the form opened, for the rename message. */
    const [storedName, setStoredName] = useState<string>()
    /**
     * The stored folder as SharePoint knows it. Cleared the moment the lookup says it is gone:
     * from then on there is nothing left to rename, only a replacement to create (HAM-4357).
     */
    const [storedId, setStoredId] = useState<string | undefined>(props.itemId)
    /** The stored folder was deleted outside HAMMOCK, so the entity is about to point elsewhere. */
    const [storedMissing, setStoredMissing] = useState<boolean>(false)
    /** Path of the parent folder, shown as the read-only prefix. */
    const [parentPath, setParentPath] = useState<string>()
    /** The parent itself is gone; creating or renaming under it could only fail on save. */
    const [parentMissing, setParentMissing] = useState<boolean>(false)
    /**
     * Path of the anchor the caller fixed, which is what the browse dialog shows at its root. It
     * has to keep naming the anchor after the user browsed into a subfolder: the dialog still
     * lists the anchor's children there, so labelling that row with the chosen subfolder would
     * name a folder it does not show.
     */
    const [anchorPath, setAnchorPath] = useState<string>()
    const [browsing, setBrowsing] = useState<boolean>(false)
    /** Whether the user typed in the field themselves; drives whether an empty name reads as an error. */
    const [edited, setEdited] = useState<boolean>(false)
    /**
     * Folder picked in the browse dialog, always the anchor itself or something below it. Lets the
     * user nest deeper without ever being able to leave the anchor the caller fixed.
     */
    const [parentOverride, setParentOverride] = useState<DriveItem>()

    const parentId = parentOverride?.id ?? props.parentId

    /** Set once the user edits the field, so a late suggestion never overwrites their typing. */
    const touched = useRef<boolean>(false)
    const debounceTimer = useRef<ReturnType<typeof setTimeout>>()
    const abortController = useRef<AbortController>()

    const resolve = (value: string, parent: string | undefined, storedId: string | undefined) => {
        abortController.current?.abort()
        const controller = new AbortController()
        abortController.current = controller

        const sanitised = sanitiseFolderName(value)
        if (!sanitised) {
            setMatch(undefined)
            setOccupiedBy(undefined)
            setStatus(FolderPathStatus.Empty)
            return
        }

        setStatus(FolderPathStatus.Checking)
        findFolderByName(axios, sanitised, parent, controller.signal)
            .then(async found => {
                if (controller.signal.aborted) {
                    return
                }
                if (!found) {
                    setMatch(undefined)
                    setOccupiedBy(undefined)
                    setStatus(storedId ? FolderPathStatus.Rename : FolderPathStatus.Create)
                    return
                }
                if (found.id === storedId) {
                    setMatch(found)
                    setOccupiedBy(undefined)
                    setStatus(FolderPathStatus.Unchanged)
                    return
                }
                if (!found.folder) {
                    setMatch(undefined)
                    setOccupiedBy(undefined)
                    setStatus(FolderPathStatus.NotFolder)
                    return
                }

                const occupant = await props.checkUsage?.(found, controller.signal)
                if (controller.signal.aborted) {
                    return
                }
                setMatch(found)
                setOccupiedBy(occupant)
                // An occupied folder is a dead end on purpose: we cannot know whether the user
                // wants to move the reference, merge, or simply mistyped, and every one of those
                // would quietly damage the other dossier or relation.
                setStatus(occupant ? FolderPathStatus.ExistsInUse : FolderPathStatus.ExistsFree)
            })
            .catch(error => {
                if (controller.signal.aborted || error?.name === 'CanceledError') {
                    return
                }
                setMatch(undefined)
                setOccupiedBy(undefined)
                setErrorDetail(error?.message)
                setStatus(FolderPathStatus.Error)
            })
    }

    /** Debounced so the field stays responsive; every keystroke cancels the previous check. */
    const scheduleResolve = (value: string) => {
        clearTimeout(debounceTimer.current)
        abortController.current?.abort()
        setStatus(FolderPathStatus.Checking)
        debounceTimer.current = setTimeout(() => resolve(value, parentId, storedId), CHECK_DEBOUNCE_MS)
    }

    // Resolve the stored folder once, so editing starts from the name that is actually on disk.
    useEffect(() => {
        setStoredId(props.itemId)
        setStoredMissing(false)
        if (!props.itemId) {
            return
        }
        setStatus(FolderPathStatus.Loading)
        axios
            .get<DriveItem>(`/items/${props.itemId}`, DRIVE_ITEM_CONFIG)
            .then(response => {
                touched.current = true
                setName(response.data.name)
                setStoredName(response.data.name)
                setMatch(response.data)
                // Anchor on where the folder actually is, not on where the caller would put a new
                // one. Otherwise renaming a folder that lives deeper would silently move it up.
                const actualParent = response.data.parentReference?.id
                if (actualParent && actualParent !== props.parentId) {
                    setParentOverride({ id: actualParent } as DriveItem)
                }
                setStatus(FolderPathStatus.Unchanged)
            })
            .catch(() => {
                // The stored folder is gone (moved or deleted outside HAMMOCK). Forget the id and
                // fall back to the suggestion, so the user creates a replacement instead of us
                // renaming something that is not there any more (HAM-4357).
                setStoredId(undefined)
                setStoredMissing(true)
                touched.current = false
                setName(props.suggestedName ?? '')
                resolve(props.suggestedName ?? '', parentId, undefined)
            })
    }, [props.itemId])

    // A different anchor (another CRM relation, say) invalidates whatever the user browsed to.
    useEffect(() => {
        setParentOverride(undefined)
    }, [props.parentId])

    // Show the parent as a read-only prefix so the CRM relation's folder cannot be typed away.
    // The anchor is resolved alongside it, and only once when the two are the same folder.
    useEffect(() => {
        let cancelled = false

        const resolvePath = (id?: string): Promise<{ path?: string; missing: boolean }> =>
            id
                ? axios
                      .get<DriveItem>(`/items/${id}`, DRIVE_ITEM_CONFIG)
                      .then(response => ({ path: getFullPath(response.data), missing: false }))
                      // Only a 404 proves the folder is gone. Any other failure is a lookup that
                      // did not answer, which must not be presented as a deleted folder.
                      .catch((error: AxiosError) => ({ path: undefined, missing: error.response?.status === 404 }))
                : Promise.resolve({ path: undefined, missing: false })

        resolvePath(parentId).then(async parent => {
            if (cancelled) {
                return
            }
            setParentPath(parent.path)
            setParentMissing(parent.missing)
            setAnchorPath(parentId === props.parentId ? parent.path : (await resolvePath(props.parentId)).path)
        })

        return () => {
            cancelled = true
        }
    }, [parentId, props.parentId])

    // Take the suggestion only while the user has not typed. Rebaselining a field someone is
    // editing throws away their work, so a late-arriving suggestion has to lose.
    useEffect(() => {
        if (touched.current || storedId || !props.suggestedName) {
            return
        }
        setName(props.suggestedName)
        scheduleResolve(props.suggestedName)
    }, [props.suggestedName, parentId])

    // Re-check against the new parent; the same name can be free in one folder and taken in another.
    useEffect(() => {
        if (touched.current && name) {
            scheduleResolve(name)
        }
    }, [parentId])

    useEffect(() => () => clearTimeout(debounceTimer.current), [])

    /**
     * An empty name is the state the field starts in: it would light up the moment the CRM switch
     * goes on and go out again as soon as the suggested name arrives, which reads as a glitch
     * rather than as feedback (HAM-4343). It only counts as a mistake once the user typed in the
     * field themselves, or once the form says it is saving. The intent stays undefined either way,
     * so the save is blocked regardless of whether anything is shown.
     */
    const emptyCounts = status !== FolderPathStatus.Empty || edited || !!props.validateEmpty

    const onChange = props.onChange
    useEffect(() => {
        // A parent that no longer exists blocks whatever the name resolves to, so it goes first.
        if (parentMissing) {
            onChange(undefined, translate('base.folderpath.status.parent_missing'))
            return
        }
        if (status === FolderPathStatus.Loading || status === FolderPathStatus.Checking) {
            return
        }

        const sanitised = sanitiseFolderName(name)
        const path = [parentPath, sanitised].filter(Boolean).join('/')

        if (BLOCKING_STATUSES.includes(status)) {
            // A missing name is never reported to the host: it is the state the field starts in, and
            // the host lists its messages in the form's error bar, where it would show up before the
            // user could type anything (HAM-4343). The form's own schema covers the empty case; the
            // field shows it inline once it counts. Every other blocking status names a real problem
            // with what is there, so those are reported straight away.
            onChange(undefined, status === FolderPathStatus.Empty ? undefined : statusMessage())
            return
        }

        const action =
            status === FolderPathStatus.Unchanged
                ? FolderPathAction.None
                : status === FolderPathStatus.Adopted
                  ? FolderPathAction.Adopt
                  : status === FolderPathStatus.Rename
                    ? FolderPathAction.Rename
                    : FolderPathAction.Create

        onChange({
            action,
            name: sanitised,
            parentId,
            itemId: action === FolderPathAction.Rename ? storedId : match?.id,
            path,
        })
    }, [status, match, parentPath, parentMissing])

    function statusMessage(): string | undefined {
        if (parentMissing) {
            return translate('base.folderpath.status.parent_missing')
        }
        switch (status) {
            case FolderPathStatus.Create:
                return translate(storedMissing ? 'base.folderpath.status.stored_missing.create' : 'base.folderpath.status.create')
            case FolderPathStatus.Rename:
                return translate('base.folderpath.status.rename', { from: storedName, to: sanitiseFolderName(name) })
            case FolderPathStatus.Adopted:
                return translate('base.folderpath.status.adopted')
            case FolderPathStatus.ExistsFree:
                return translate('base.folderpath.status.exists_free')
            case FolderPathStatus.ExistsInUse:
                return translate('base.folderpath.status.exists_in_use', { name: occupiedBy })
            case FolderPathStatus.NotFolder:
                return translate('base.folderpath.status.not_folder')
            case FolderPathStatus.Empty:
                return translate(storedMissing ? 'base.folderpath.status.stored_missing.empty' : 'base.folderpath.status.empty')
            case FolderPathStatus.Error:
                return translate('base.folderpath.status.error', { message: errorDetail })
        }
        return undefined
    }

    // A missing parent or a vanished stored folder is never a "not filled in yet" case, so unlike
    // the empty message they are reported right away (HAM-4343 + HAM-4357).
    const blocking = parentMissing || (BLOCKING_STATUSES.includes(status) && (emptyCounts || storedMissing))
    // ExistsFree blocks saving too, but it is a choice waiting to be made rather than a mistake,
    // so it stays a warning instead of turning the field red.
    const invalid = blocking && (parentMissing || status !== FolderPathStatus.ExistsFree)
    // Losing the stored folder is not a mistake either, but the user has to see that the link is
    // about to move to a folder that did not exist before. Only while that is what happens next:
    // once the name resolves to something else the field has its own story to tell.
    const warning = !blocking && storedMissing && status === FolderPathStatus.Create
    const message = parentMissing || storedMissing || emptyCounts ? statusMessage() : undefined

    // Everything lives inside the Field: informative outcomes as a hint, blocking ones as the
    // validation message, so the field takes the same vertical space as every other form row.
    const adoptable = blocking && !parentMissing && status === FolderPathStatus.ExistsFree
    const validationMessage = !blocking && !warning ? undefined : adoptable ? (
        <span>
            {message}{' '}
            <Button size={'small'} appearance={'transparent'} className={styles.inlineAction} onClick={() => setStatus(FolderPathStatus.Adopted)}>
                {translate('base.folderpath.action.use_existing')}
            </Button>
        </span>
    ) : (
        message
    )

    return (
        <>
            <Field
                label={props.label ?? translate('base.folderpath.label')}
                required={props.required}
                validationState={blocking ? (invalid ? 'error' : 'warning') : warning ? 'warning' : 'none'}
                validationMessage={validationMessage}
                hint={!blocking && !warning ? message : undefined}
            >
                <Input
                    disabled={props.disabled || status === FolderPathStatus.Loading}
                    value={name}
                    contentBefore={
                        <Text className={styles.prefix} size={200} title={parentPath ? `/${parentPath}/` : '/'}>
                            {parentPath ? `/${parentPath}/` : '/'}
                        </Text>
                    }
                    contentAfter={
                        status === FolderPathStatus.Checking || status === FolderPathStatus.Loading ? (
                            <LoadingIcon />
                        ) : (
                            <Button
                                size={'small'}
                                appearance={'subtle'}
                                icon={<BrowseIcon />}
                                disabled={props.disabled}
                                title={translate('base.folderpath.browse')}
                                onClick={() => setBrowsing(true)}
                            />
                        )
                    }
                    onChange={(_, data) => {
                        // Deliberately not sanitised here: rewriting the value under the cursor is
                        // what made the old field unpleasant to type in. That happens on blur.
                        touched.current = true
                        setEdited(true)
                        setName(data.value)
                        scheduleResolve(data.value)
                    }}
                    onBlur={() => {
                        const sanitised = sanitiseFolderName(name)
                        if (sanitised !== name) {
                            setName(sanitised)
                            scheduleResolve(sanitised)
                        }
                    }}
                />
            </Field>
            {browsing && (
                <FolderBrowseDialog
                    rootId={props.parentId}
                    rootLabel={anchorPath ? `/${anchorPath}` : '/'}
                    selectedId={parentId}
                    onDismiss={() => setBrowsing(false)}
                    onSelect={folderId => {
                        setBrowsing(false)
                        setParentOverride(folderId ? ({ id: folderId } as DriveItem) : undefined)
                    }}
                />
            )}
        </>
    )
}
